import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  fetchMembers,
  inviteMember,
  removeMember,
  updateMemberRole,
} from "../../../redux/actions/organizationAction";
import { canManageMembers, getCurrentRole } from "../../../utils/rbac";
import { workEmailValidator } from "../../../utils/workEmail";
import {
  getActiveOrganizationId,
  normalizeId,
} from "../../../utils/organizationId";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

export default function TeamSettings() {
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [form] = Form.useForm();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const organizationId = getActiveOrganizationId();
  const canManage = canManageMembers();
  const actorRole = getCurrentRole();

  const loadMembers = useCallback(() => {
    if (!organizationId) return;
    setLoading(true);
    fetchMembers(organizationId)
      .then((response) => {
        if (response.status === 200) {
          setMembers(response.data.result.members || []);
          setPendingInvites(response.data.result.pendingInvites || []);
        }
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const onInvite = async (values) => {
    if (!organizationId) {
      message.error(
        "No active organization. Please log out and sign in again.",
      );
      return;
    }
    setInviteLoading(true);
    try {
      await inviteMember(organizationId, values);
      message.success("Invite sent.");
      setInviteOpen(false);
      form.resetFields();
      loadMembers();
    } catch (e) {
      // apiHelper shows error
    } finally {
      setInviteLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, row) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") || "—",
    },
    { title: "Email", dataIndex: "emailAddress", key: "email" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role, row) =>
        canManage &&
        row.role !== "owner" &&
        row.userId !== normalizeId(user.id) ? (
          <Select
            value={role}
            style={{ width: 120 }}
            options={ROLE_OPTIONS.filter(
              (opt) => actorRole === "owner" || opt.value !== "owner",
            )}
            onChange={(value) => {
              updateMemberRole(organizationId, row.userId, value).then(() => {
                message.success("Role updated.");
                loadMembers();
              });
            }}
          />
        ) : (
          <Tag>{role}</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    },
    canManage
      ? {
          title: "Actions",
          key: "actions",
          render: (_, row) =>
            row.role !== "owner" && row.userId !== normalizeId(user.id) ? (
              <Button
                danger
                type="link"
                onClick={() => {
                  Modal.confirm({
                    title: "Remove member?",
                    content: `Remove ${row.emailAddress} from this organization?`,
                    onOk: () =>
                      removeMember(organizationId, row.userId).then(() => {
                        message.success("Member removed.");
                        loadMembers();
                      }),
                  });
                }}
              >
                Remove
              </Button>
            ) : null,
        }
      : null,
  ].filter(Boolean);

  return (
    <div style={{ margin: "1.5rem 2rem" }}>
      <Card
        title="Team members"
        extra={
          canManage ? (
            <Button type="primary" onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          ) : null
        }
      >
        <Typography.Paragraph type="secondary">
          Manage who has access to this organization and their roles.
        </Typography.Paragraph>
        <Table
          rowKey={(row) => String(row.userId)}
          loading={loading}
          dataSource={members}
          columns={columns}
          pagination={false}
        />
        {pendingInvites.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: 24 }}>
              Pending invites
            </Typography.Title>
            <Space direction="vertical">
              {pendingInvites.map((invite) => (
                <Typography.Text key={invite.id}>
                  {invite.email} — {invite.role} (expires{" "}
                  {new Date(invite.expiresAt).toLocaleDateString()})
                </Typography.Text>
              ))}
            </Space>
          </>
        )}
      </Card>

      <Modal
        title="Invite team member"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onInvite}>
          <Form.Item
            name="email"
            label="Work email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
              { validator: workEmailValidator },
            ]}
          >
            <Input placeholder="colleague@company.com" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            initialValue="editor"
            rules={[{ required: true }]}
          >
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={inviteLoading}
            block
          >
            Send invite
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
